import React, { ReactNode, useState } from "react";
import Big from "big.js";
import {
  Avatar,
  Button,
  Col,
  Row,
  Space,
  Switch,
  Tooltip as AntdTooltip,
  Typography,
  notification,
} from "antd";
import { Link, RouteComponentProps } from "react-router-dom";
import {
  FUNDS_PENDING_STAKED_ETH,
  FUNDS_STAKING_FORM,
  FUNDS_TIMELOCK_STAKING_FORM,
  FUNDS_UNSTAKING_FORM,
} from "@funds/config";
import FundsService from "@services/Funds.service";
import { StakingEthResponse } from "@funds/pages/MyFunds/tabs/FundsTab/parts/CurrencyDetails";
import SecondaryMarketService from "@services/SecondaryMarket.service";
import Tooltip from "@funds/pages/MyFunds/parts/Tooltip";
import StatsItem from "../StatsItem";
import EthIcon from "@funds/pages/MyFunds/tabs/FundsTab/parts/CurrencyAvatar/icons/ethereum-(eth).svg";
import ListIcon from "./list.svg";
import CoinsIcon from "./coins-hand.svg";
import Loader from "@appComponents/Loader/Loader";
import EmptyState from "../EmptyState/EmptyState";
import Modal from "@funds/components/Modal/Modal";
import Chevron from "@images/chevron.svg";
import { RiskModalProps } from "@funds/pages/MyFunds/utils";
import InvestmentRiskModal from "@funds/components/InvestmentRiskModal/InvestmentRiskModal";
import { TUserAgreements } from "@features/reducer.info";
import { AGREEMENT_TYPES } from "@services/UserAgreements.service";
import { isTier1AndAbove } from "@funds/features/helpers/functions";

export const tier1RequiredMessage =
  "You need to be at least tier 1 to access this feature";
export const stakeRestrictionMessage =
  "This feature is currently restricted on your account";

type Props = {
  appLink: string;
  investorId: number;
  investorCountry?: string;
  investorAgreements?: TUserAgreements;
  history: RouteComponentProps<{}>["history"];
  location: RouteComponentProps<{}>["location"];
  tier?: string;
  canStake?: boolean;
};

type StatsConfigArgs = {
  isUk?: boolean;
  agreements?: TUserAgreements;
  basePath: string;
  setRiskModalProps: (props: RiskModalProps) => void;
  isTier1?: boolean;
  canStake?: boolean;
};

const statsConfig: (args: StatsConfigArgs) => Record<
  string,
  {
    title: React.ReactNode;
    order: number;
    action: (
      url: string | string[],
      fn?: Function,
      restaking?: boolean,
      isVisible?: boolean
    ) => ReactNode;
  }
> = ({ isUk, agreements, basePath, setRiskModalProps, isTier1, canStake }) => ({
  deposited_eth: {
    title: (
      <Space style={{ justifyContent: "space-between" }}>
        <Typography.Title style={{ textTransform: "uppercase" }} level={5}>
          Available ETH balance
        </Typography.Title>
        <Avatar
          icon={<EthIcon />}
          style={{
            backgroundColor: "#1C80F5",
            lineHeight: "46px",
            width: "36px",
            height: "36px",
          }}
        />
      </Space>
    ),
    order: 1,
    action: (url: string) => (
      <AntdTooltip
        title={
          !canStake
            ? stakeRestrictionMessage
            : !isTier1
            ? tier1RequiredMessage
            : ""
        }
      >
        <span />
        <Link
          to={url}
          onClick={(e) => {
            if (isUk && !agreements?.eth_staking) {
              e.preventDefault();
              setRiskModalProps({
                agreementType: AGREEMENT_TYPES.ETH_STAKING,
                redirectionPath: `${basePath}/${FUNDS_STAKING_FORM}`,
              });
            }
          }}
        >
          <Button
            style={
              isUk
                ? {
                    background: !isTier1 ? "" : "transparent",
                    border: "1px solid #E4E4E4",
                    color: !isTier1 ? "" : "#323232",
                    letterSpacing: 0,
                    visibility: "visible",
                  }
                : { letterSpacing: 0 }
            }
            type="primary"
            disabled={!isTier1 || !canStake}
          >
            Stake
          </Button>
        </Link>
      </AntdTooltip>
    ),
  },
  active_staked_eth: {
    title: (
      <Space style={{ justifyContent: "space-between" }}>
        <Space
          wrap={false}
          align="start"
          style={{ justifyContent: "flex-start", alignItems: "center" }}
        >
          <Typography.Title style={{ textTransform: "uppercase" }} level={5}>
            Staked ETH balance
          </Typography.Title>
          <Tooltip
            title=""
            tooltipMessage="Your Staked_ETH Balance represents the ETH that are staked and actively earning rewards"
          />
        </Space>

        <Avatar
          icon={<EthIcon />}
          style={{
            backgroundColor: "#81109C",
            lineHeight: "46px",
            width: "36px",
            height: "36px",
          }}
        />
      </Space>
    ),
    order: 2,
    action: (url: string[]) => (
      <>
        {process.env.UNSTAKING_ENABLED && (
          <AntdTooltip title={!isTier1 ? tier1RequiredMessage : ""}>
            <span />
            <Link
              to={url[0]}
              style={{
                pointerEvents: "auto",
              }}
            >
              <Button
                style={{
                  background: !isTier1 ? "" : "transparent",
                  border: "1px solid #E4E4E4",
                  color: !isTier1 ? "" : "#323232",
                  letterSpacing: 0,
                  visibility: "visible",
                  marginBottom: 8,
                }}
                disabled={!isTier1}
                type="primary"
              >
                Unstake
              </Button>
            </Link>
          </AntdTooltip>
        )}

        <AntdTooltip
          title={
            !isTier1
              ? tier1RequiredMessage
              : "Select this option to timelock your available Staked ETH for lower fees and additional benefits"
          }
        >
          <span />
          <Link to={url[1]}>
            <Button
              style={{
                background: !isTier1 ? "" : "transparent",
                border: "1px solid #E4E4E4",
                color: !isTier1 ? "" : "#323232",
                letterSpacing: 0,
                visibility: "visible",
              }}
              type="primary"
              disabled={!isTier1}
            >
              Timelock Staked ETH
            </Button>
          </Link>
        </AntdTooltip>
      </>
    ),
  },
  pending_staked_eth: {
    title: (
      <Space style={{ justifyContent: "space-between" }}>
        <Typography.Title style={{ textTransform: "uppercase" }} level={5}>
          Staking requests
        </Typography.Title>
        <Avatar
          icon={<ListIcon />}
          style={{
            backgroundColor: "#1AC4CF",
            lineHeight: "40px",
            width: "36px",
            height: "36px",
          }}
        />
      </Space>
    ),
    order: 3,
    action: (url: string) => (
      <Link to={url}>
        <Button
          type="primary"
          style={{
            letterSpacing: 0,
            background: "transparent",
            border: "1px solid #E4E4E4",
            color: "#323232",
          }}
        >
          <Space align="center" style={{ justifyContent: "center" }}>
            <span>Staking list</span>
            <Chevron />
          </Space>
        </Button>
      </Link>
    ),
  },
  rewards_eth: {
    title: (
      <Space style={{ justifyContent: "space-between" }}>
        <Typography.Title style={{ textTransform: "uppercase" }} level={5}>
          Total Staking rewards
        </Typography.Title>
        <Avatar
          icon={<CoinsIcon />}
          style={{
            backgroundColor: "#2EC973",
            lineHeight: "46px",
            width: "36px",
            height: "36px",
          }}
        />
      </Space>
    ),
    order: 4,
    action: (url: string, onChange: () => void, restaking, isVisible) => {
      const basicStyles = { justifyContent: "flex-start", margin: "11px auto" };

      const hiddenStyles = {
        visibility: "hidden",
        pointerEvents: "none",
      };

      const styles = isVisible
        ? basicStyles
        : { ...basicStyles, ...hiddenStyles };

      return (
        <div key={url}>
          <Space style={styles}>
            <>
              <Switch
                disabled={!isTier1}
                onChange={onChange}
                checked={restaking}
              />
              <span>Auto-Restaking</span>
              <Tooltip
                title=""
                placement="topLeft"
                showArrow={false}
                tooltipMessage="Enable Auto-Restaking to automatically reinvest your ETH rewards received every Monday. Your earnings will be automatically staked without any manual intervention. You can always adjust this setting later from the Staking Dashboard."
              />
            </>
          </Space>
        </div>
      );
    },
  },
});

const Stats = ({
  appLink,
  investorId,
  investorCountry,
  history,
  location,
  investorAgreements,
  tier,
  canStake,
}: Props) => {
  const [stakingData, setStakingData] = useState<StakingEthResponse>();
  const [ethUsdExchangeRate, setEthUsdExchangeRate] = useState();
  const [loading, setLoading] = useState(true);
  const [modalVisibility, setModalVisibility] = useState(false);
  const [autoRestaking, setAutoRestaking] = useState(false);
  const [riskModalProps, setRiskModalProps] = useState<
    RiskModalProps | undefined
  >();

  const basePath = `${location.pathname.split("/funds")[0]}/funds`;

  const isTier1 = !isTier1AndAbove(tier ?? "");

  const isUk = React.useMemo(
    () =>
      investorCountry === "GB" &&
      process.env.UK_RISK_MODAL_ENABLED?.toLowerCase() === "true",
    [investorCountry]
  );

  React.useEffect(() => {
    Promise.all([
      SecondaryMarketService.getExchange(),
      FundsService.getStakingEth(investorId),
      FundsService.getAutoStaking(),
    ]).then(([respExchange, respStaking, respAutoStaking]) => {
      setStakingData(respStaking.data);
      setEthUsdExchangeRate(respExchange.data.usd_eth);
      setLoading(false);
      setAutoRestaking(respAutoStaking.data.auto_restaking);
    });
  }, []);

  const getUrl = (key: string) => {
    switch (key) {
      case "deposited_eth":
        return `${appLink}/${FUNDS_STAKING_FORM}`;

      case "active_staked_eth":
        return [
          `${appLink}/${FUNDS_UNSTAKING_FORM}`,
          `${appLink}/${FUNDS_TIMELOCK_STAKING_FORM}`,
        ];

      case "pending_staked_eth":
        return `${appLink}/${FUNDS_PENDING_STAKED_ETH}`;

      default:
        return "";
    }
  };

  if (loading || !stakingData || !ethUsdExchangeRate) return <Loader />;

  if (
    stakingData.pending_staked_eth === 0 &&
    stakingData.active_staked_eth === 0 &&
    stakingData.rewards_eth === 0
  )
    return (
      <>
        <EmptyState
          shouldOpenRiskWarningModal={isUk && !investorAgreements?.eth_staking}
          isUk={isUk}
          handleOpenRiskWarningModal={setRiskModalProps}
        />

        <InvestmentRiskModal
          history={history}
          isOpen={!!riskModalProps}
          onClose={() => setRiskModalProps(undefined)}
          {...riskModalProps}
        />
      </>
    );

  return (
    <div style={{ margin: "20px auto 10px" }}>
      <Row gutter={[8, 8]}>
        {Object.keys(stakingData)
          .filter(
            (key) =>
              statsConfig({
                isUk,
                basePath,
                setRiskModalProps,
                agreements: investorAgreements,
                isTier1,
                canStake,
              })[key]
          )
          .map((key) => {
            const amount = stakingData[key].toString();

            const { title, order, action } = statsConfig({
              isUk,
              basePath,
              setRiskModalProps,
              agreements: investorAgreements,
              isTier1,
              canStake,
            })[key];
            const amountUsd = Big(amount).times(ethUsdExchangeRate).toString();
            const url = getUrl(key);

            let updatedAction = action(url);
            let data = [{ amount, amountUsd }];

            const isVisible = Big(stakingData.available_staked_eth).gt(0);

            if (key === "rewards_eth") {
              updatedAction = action(
                url,
                () => {
                  setModalVisibility(true);
                },
                autoRestaking,
                isVisible
              );
            }

            if (key === "active_staked_eth") {
              const availableKey = "available_staked_eth";
              const availableAmount = Big(stakingData[availableKey]).toString();

              const timelockedKey = "timelocked_staked_eth";
              const timelockedAmount = Big(
                stakingData[timelockedKey]
              ).toString();

              data = [
                {
                  title: "Available",
                  amount: availableAmount,
                  amountUsd: Big(availableAmount)
                    .times(ethUsdExchangeRate)
                    .toString(),
                  currency: "Staked_ETH",
                  upperCase: false,
                },
                {
                  title: "Timelocked",
                  amount: timelockedAmount,
                  amountUsd: Big(timelockedAmount)
                    .times(ethUsdExchangeRate)
                    .toString(),
                  currency: "Staked_ETH",
                  upperCase: false,
                },
              ] as any;
            }

            if (key === "pending_staked_eth") {
              const pendingKey = "pending_staked_eth";
              const pendingAmount = Big(stakingData[pendingKey]).toString();

              const activeKey = "active_staked_eth";
              const activeAmount = Big(stakingData[activeKey]).toString();

              data = [
                {
                  title: "Pending",
                  amount: pendingAmount,
                  amountUsd: Big(pendingAmount)
                    .times(ethUsdExchangeRate)
                    .toString(),
                  currency: "Staked_ETH",
                  upperCase: false,
                },
                {
                  title: "Active",
                  amount: activeAmount,
                  amountUsd: Big(activeAmount)
                    .times(ethUsdExchangeRate)
                    .toString(),
                },
              ] as any;
            }

            return {
              title,
              order,
              action: updatedAction,
              data,
              fieldName: key,
            };
          })
          .sort((a, b) => a.order - b.order)
          .map((props) => (
            <Col
              data-cy={`${props.fieldName}-card`}
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={6}
              xxl={6}
              key={props.fieldName}
            >
              <StatsItem {...props} key={props.fieldName} />
            </Col>
          ))}
      </Row>
      <Modal
        onConfirm={async () => {
          await FundsService.putAutoStaking(!autoRestaking);
          notification.success({
            message: `Auto-Restaking ${autoRestaking ? "disabled" : "enabled"}`,
          });
          setAutoRestaking(!autoRestaking);
          setModalVisibility(false);
        }}
        open={modalVisibility}
        onCancel={() => setModalVisibility(false)}
      >
        {autoRestaking ? (
          <>
            <Typography.Title level={4}>
              Are you sure you want to stop Auto-Restaking?
            </Typography.Title>
            <Typography.Text>
              Disabling this feature means your ETH rewards will no longer be
              automatically reinvested each Monday. You can re-enable
              Auto-Restaking anytime from the Staking Dashboard if you wish to
              continue growing your earnings effortlessly.
            </Typography.Text>
          </>
        ) : (
          <>
            <Typography.Title level={4}>
              Are you sure you want to start Auto-Restaking?
            </Typography.Title>
            <Typography.Text>
              Enable Auto-Restaking to automatically reinvest your ETH rewards
              received every Monday. Your earnings will be automatically staked
              without any manual intervention. You can always adjust this
              setting later from the Staking Dashboard.
            </Typography.Text>
          </>
        )}
      </Modal>

      <InvestmentRiskModal
        history={history}
        isOpen={!!riskModalProps}
        onClose={() => setRiskModalProps(undefined)}
        {...riskModalProps}
      />
    </div>
  );
};

export default Stats;
